/**
 * Collapses the route's URL state + lifted filter values into the chip
 * list rendered by `ActiveFilterChips` on the left of the table toolbar.
 *
 * Three chip sources are folded in, in this order:
 *   1. Issue-area selection (parent tile) + active sub-axis (Reason chip).
 *      The parent shows up as a non-removable `path-parent` chip when a
 *      sub-axis is active; removing the sub (or the parent on its own)
 *      walks the URL back through `strip.applySelection`.
 *   2. Summary-chip key (when one is selected on the panel chip row).
 *   3. Each filter from `config.filters`: one chip per selected
 *      multiselect option, one for a non-default select, one for an
 *      enabled toggle, one for a non-empty search term.
 */
import type {
  ExceptionConfig,
  ExceptionFilterDef,
  ExceptionFilterOption,
  ExceptionFilterStripDef,
  IssueCardEntry,
} from '../../../config/exceptions/types'
import type { ActiveFilterChip } from './ActiveFilterChips'

interface BuildArgs {
  strips: ExceptionFilterStripDef[]
  config: ExceptionConfig
  response: Record<string, any> | null
  filterCounts: Record<string, number> | null
  searchParams: URLSearchParams
  setSearchParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void
  filterValues: Record<string, any>
  updateFilterValue: (key: string, value: any) => void
  filterEnabled: boolean
  setFilterEnabled: (next: boolean) => void
  summaryChips?: ExceptionConfig['summaryChips']
  activeChipKey: string | null
  setActiveChipKey: (key: string | null) => void
  /**
   * Multi-select state. When ``summaryChips.multiSelect`` is true the
   * builder pushes one removable chip per active key (matching how
   * multiselect filters render). When false / unset, the builder falls
   * back to the single-key ``activeChipKey`` behaviour.
   */
  activeChipKeys?: string[]
  setActiveChipKeys?: (keys: string[]) => void
  serverSearch?: { value: string; onChange: (value: string) => void }
  /**
   * Row data for resolving multiselect filters that declare `extractOptions`
   * instead of static `options`. Without this, an active selection like
   * `__no_category__` falls back to its raw value in the chip label.
   */
  rows?: Record<string, any>[]
}

const defaultIsCardActive = (
  cardKey: string | null,
  activeKey: string | null,
): boolean => cardKey === activeKey

const findActiveCard = (
  cards: IssueCardEntry[],
  activeKey: string | null,
  isCardActive: (cardKey: string | null, activeKey: string | null) => boolean,
): { parent: IssueCardEntry | null; sub: IssueCardEntry | null } => {
  let parent: IssueCardEntry | null = null
  let sub: IssueCardEntry | null = null

  for (const card of cards) {
    if (card.subCards && card.subCards.cards.length > 0) {
      if (isCardActive(card.key, activeKey)) {
        parent = card
        const matchingSub = card.subCards.cards.find(
          (s) => s.key === activeKey,
        )
        if (matchingSub) sub = matchingSub
        break
      }
    }
  }

  if (!parent) {
    // No sub-axis breakdown was hit. Look for a direct parent match (the
    // user clicked a leaf tile like "Line items" that has no sub-cards).
    const direct = cards.find(
      (c) => c.key !== null && isCardActive(c.key, activeKey),
    )
    if (direct) parent = direct
  }

  return { parent, sub }
}

export const buildActiveFilterChips = ({
  strips,
  config,
  response,
  filterCounts,
  searchParams,
  setSearchParams,
  filterValues,
  updateFilterValue,
  filterEnabled,
  setFilterEnabled,
  summaryChips,
  activeChipKey,
  setActiveChipKey,
  activeChipKeys,
  setActiveChipKeys,
  serverSearch,
  rows,
}: BuildArgs): ActiveFilterChip[] => {
  const chips: ActiveFilterChip[] = []

  // ── Issue-area path (parent › sub) ───────────────────────────────
  for (const strip of strips) {
    if (strip.type !== 'cards') continue
    const cards = strip.buildCards(response, filterCounts)
    if (cards.length === 0) continue
    const activeKey = strip.getActiveKey(searchParams)
    if (activeKey == null) continue
    const isCardActive = strip.isCardActive ?? defaultIsCardActive
    const { parent, sub } = findActiveCard(cards, activeKey, isCardActive)
    if (!parent) continue

    const writeKey = (key: string | null) => {
      const next = strip.applySelection(searchParams, key)
      if (next.toString() === searchParams.toString()) return
      next.delete('page')
      setSearchParams(next, { replace: false })
    }

    if (sub) {
      chips.push({
        key: `path-parent-${parent.key ?? 'root'}`,
        label: parent.label,
        kind: 'path-parent',
        onRemove: () => writeKey(null),
      })
      chips.push({
        key: `path-sub-${sub.key ?? 'root'}`,
        label: sub.label,
        kind: 'path-sub',
        onRemove: () => writeKey(parent.key),
      })
    } else {
      chips.push({
        key: `path-parent-${parent.key ?? 'root'}`,
        label: parent.label,
        kind: 'path-parent',
        onRemove: () => writeKey(null),
      })
    }
    // One issue-area path per page, even if multiple card strips exist.
    break
  }

  // ── Summary-chip key (panel chip row) ────────────────────────────
  if (summaryChips?.multiSelect) {
    const selected = activeChipKeys ?? []
    for (const key of selected) {
      const category = summaryChips.categories.find((c) => c.key === key)
      if (!category) continue
      chips.push({
        key: `summary-${category.key}`,
        label: category.label,
        kind: 'filter',
        onRemove: setActiveChipKeys
          ? () => setActiveChipKeys(selected.filter((k) => k !== key))
          : undefined,
      })
    }
  } else if (summaryChips && activeChipKey) {
    const category = summaryChips.categories.find((c) => c.key === activeChipKey)
    if (category) {
      chips.push({
        key: `summary-${category.key}`,
        label: category.label,
        kind: 'filter',
        onRemove: () => setActiveChipKey(null),
      })
    }
  }

  // ── Generic filters (search / multiselect / select / toggle) ─────
  for (const f of config.filters ?? []) {
    if (f.type === 'multiselect') {
      const selected = (filterValues[f.key] ?? []) as string[]
      if (selected.length === 0) continue
      const options: ExceptionFilterOption[] =
        f.options ?? (f.extractOptions ? f.extractOptions(rows ?? []) : [])
      const labelFor = (v: string) =>
        options.find((o) => o.value === v)?.label ?? v
      for (const v of selected) {
        chips.push({
          key: `filter-${f.key}-${v}`,
          label: `${f.label}: ${labelFor(v)}`,
          kind: 'filter',
          onRemove: () =>
            updateFilterValue(
              f.key,
              selected.filter((x) => x !== v),
            ),
        })
      }
    } else if (f.type === 'select') {
      const defaultV = f.defaultValue ?? 'all'
      const v = (filterValues[f.key] ?? defaultV) as string
      if (v === defaultV) continue
      const labelFor = (val: string) =>
        f.options.find((o) => o.value === val)?.label ?? val
      chips.push({
        key: `filter-${f.key}-${v}`,
        label: `${f.label}: ${labelFor(v)}`,
        kind: 'filter',
        onRemove: () => updateFilterValue(f.key, defaultV),
      })
    } else if (f.type === 'toggle') {
      const enabled = !!(filterValues[f.key] ?? f.defaultEnabled)
      // Only chip when the toggle differs from its default. Defaulting
      // ON is rare but possible; either direction reads as "an active
      // filter the user can clear".
      if (enabled === !!f.defaultEnabled) continue
      chips.push({
        key: `filter-${f.key}`,
        label: f.label,
        kind: 'filter',
        onRemove: () => updateFilterValue(f.key, !!f.defaultEnabled),
      })
    } else if (f.type === 'search') {
      const isServer = !!serverSearch
      const term = isServer
        ? (serverSearch?.value ?? '')
        : (((filterValues[f.key] ?? '') as string) || '')
      const trimmed = term.trim()
      if (!trimmed) continue
      chips.push({
        key: `filter-${f.key}`,
        label: `${f.label}: ${trimmed}`,
        kind: 'filter',
        onRemove: () => {
          if (isServer) serverSearch?.onChange('')
          else updateFilterValue(f.key, '')
        },
      })
    }
  }

  // ── filterToggle (the special teal switch above the table) ───────
  if (config.filterToggle) {
    const defaultEnabled = !!config.filterToggle.defaultEnabled
    if (filterEnabled !== defaultEnabled) {
      chips.push({
        key: 'filter-toggle',
        label: config.filterToggle.label,
        kind: 'filter',
        onRemove: () => setFilterEnabled(defaultEnabled),
      })
    }
  }

  return chips
}
